// components/auth/session-expired-modal.tsx
// Spec 0015, extended per live feedback (2026-09-07): the moment the
// session hits zero, this shows a full screen, dimmed, unclosable modal
// (no backdrop click, no Escape) instead of silently redirecting — the
// employee sees clearly why they're being signed out. It auto signs out a
// few seconds later on its own; the button just does it immediately for
// anyone who doesn't want to wait. Mounted exactly once, app wide
// (wrapper-client-body.tsx), so it can never show twice and there is only
// ever one performSignOut("expired") call site for the automatic path.
"use client";

import { useSessionCountdown } from "@/hooks/use-session-countdown";
import { _t } from "@/lib/utils";
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from "@headlessui/react";
import { ClockIcon } from "@heroicons/react/24/outline";
import { useEffect, useRef, useState } from "react";
import { performSignOut } from "./logout-button";

const AUTO_SIGN_OUT_AFTER_SECONDS = 5;

export default function SessionExpiredModal() {
  const { expired } = useSessionCountdown();
  const [secondsLeft, setSecondsLeft] = useState(AUTO_SIGN_OUT_AFTER_SECONDS);
  const signedOutRef = useRef(false);

  useEffect(() => {
    if (!expired) {
      setSecondsLeft(AUTO_SIGN_OUT_AFTER_SECONDS);
      signedOutRef.current = false;
      return;
    }

    const interval = setInterval(() => {
      setSecondsLeft((prev) => {
        const next = prev - 1;
        if (next <= 0 && !signedOutRef.current) {
          signedOutRef.current = true;
          performSignOut("expired");
        }
        return next;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [expired]);

  const signOutNow = () => {
    if (signedOutRef.current) return;
    signedOutRef.current = true;
    performSignOut("expired");
  };

  return (
    // No onClose handler passed through to a dismiss action: Dialog needs
    // one prop-wise, but nothing here ever calls it (no backdrop button, no
    // Escape handling added), so the dialog cannot be dismissed any way
    // other than the sign out actions below.
    <Dialog open={expired} onClose={() => {}} className="relative z-[100]">
      <DialogBackdrop className="fixed inset-0 bg-black/70 backdrop-blur-sm transition-opacity" />
      <div className="fixed inset-0 flex w-screen items-center justify-center p-4">
        <DialogPanel className="w-full max-w-sm rounded-2xl bg-white dark:bg-gray-900 p-6 text-center shadow-2xl">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
            <ClockIcon className="h-6 w-6 text-red-600 dark:text-red-400" aria-hidden="true" />
          </div>
          <DialogTitle as="h3" className="mt-4 text-base font-semibold text-gray-900 dark:text-white">
            {_t("session_expired_modal_title")}
          </DialogTitle>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            {_t("session_expired_modal_body").replace("{seconds}", String(Math.max(0, secondsLeft)))}
          </p>
          <button
            type="button"
            onClick={signOutNow}
            className="mt-5 w-full rounded-md bg-primary-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-primary-700 transition-colors"
          >
            {_t("session_expired_modal_sign_out_now")}
          </button>
        </DialogPanel>
      </div>
    </Dialog>
  );
}
